/**
 * Dimensional quality control (schema DIM).
 *
 * The test shack measures a sample during rolling and checks it against the section's
 * tolerance bands. Which dimensions apply depends on the profile: of the 82 Mill #2
 * sections loaded from the legacy Access application, 56 measure all twelve, 19 measure
 * eight (no ALT/RLT/TALT/TRLT), five measure six and one measures ten. That is why the
 * readings travel as a list and not as twelve fixed fields.
 */

export const TOLERANCE_STATUS = {
	IN: "In",
	OVER: "Over",
	UNDER: "Under",
	NO_SPEC: "NoSpec",
} as const;

type ToleranceStatus = (typeof TOLERANCE_STATUS)[keyof typeof TOLERANCE_STATUS];

export interface Inspection {
	ProfileInspectionId: number;
	AssetId: number;
	SectionId: number;
	SectionName: string;
	HeatId: number | null;
	HeatName: string | null;
	CampaignId: number | null;
	ProfileSpecId: number;
	SpecRev: number;
	SpecSubRev: number | null;
	InspectedAt: Date;
	Inspector: string | null;
	ShiftId: number | null;
	MeasuredLbsPerFoot: number | null;
	TargetLbsPerFoot: number | null;
	MinLbsPerFoot: number | null;
	MaxLbsPerFoot: number | null;
	LegDeltaMax: number | null;
	DeltaTopLegs: number | null;
	DeltaBottomLegs: number | null;
	WeightStatus: ToleranceStatus | null;
	Comments: string | null;
	CreatedBy: string;
	CreatedAt: string;
	UpdatedBy: string | null;
	UpdatedAt: string | null;
}

export interface InspectionValue {
	ProfileInspectionId: number;
	ProfileItemId: number;
	ProfileCategoryId: number;
	CategoryName: string;
	CategorySortOrder: number;
	Code: string;
	ValueTypeCode: string;
	SortOrder: number;
	NumericValue: number | null;
	Checked: boolean | null;
	Ltl: number | null;
	Nominal: number | null;
	Utl: number | null;
	Status: ToleranceStatus;
}

/** One sample plus its readings keyed by ProfileItemId, the shape the grid and the form both work with. */
export interface InspectionWithValues extends Inspection {
	values: Record<number, InspectionValue>;
}

export interface ShiftInspection extends InspectionWithValues {
	MeasuredItemCount: number;
	OutOfSpecCount: number;
	/** Same rule as the report's "No Specification" chip. */
	HasTolerances: boolean;
}

export interface CurrentShiftMetadata {
	ShiftId: number;
	Start: Date;
	End: Date;
}

export interface ShiftInspectionsResult {
	inspections: ShiftInspection[];
	shift: CurrentShiftMetadata | null;
}

export interface ApplicableInspectionItem {
	ProfileItemId: number;
	Code: string;
	SortOrder: number;
	/** Undefined while the SP does not return limits yet. */
	Ltl?: number | null;
	Utl?: number | null;
}

export interface ProfileInspectionsResult {
	inspections: InspectionWithValues[];
	currentShift: CurrentShiftMetadata;
	applicableItemsBySpec: Record<number, ApplicableInspectionItem[] | undefined>;
}

export type InspectionReadingPayload = { ProfileItemId: number } & (
	| { NumericValue: number; Checked?: never }
	| { Checked: boolean; NumericValue?: never }
);

export interface UpsertInspectionPayload {
	profileInspectionId: number | null;
	assetId: number;
	sectionId: number;
	heatId: number | null;
	heatName: string | null;
	inspectedAt: Date;
	measuredLbsPerFoot: number | null;
	weightProfileItemId: number | null;
	comments: string | null;
	readings: InspectionReadingPayload[];
}

/** Where a reading sits against its band. Mirrors the CASE in DIM.GetProfileInspections. */
export const statusOf = (
	value: number | null | undefined,
	ltl: number | null | undefined,
	utl: number | null | undefined,
): ToleranceStatus => {
	if (value === null || value === undefined) return TOLERANCE_STATUS.NO_SPEC;
	if (ltl == null && utl == null) return TOLERANCE_STATUS.NO_SPEC;
	if (utl != null && value > utl) return TOLERANCE_STATUS.OVER;
	if (ltl != null && value < ltl) return TOLERANCE_STATUS.UNDER;
	return TOLERANCE_STATUS.IN;
};

/**
 * DELTA TOP / BOTTOM LEGS of the Access form. Confirmed against the application itself:
 * the two calculated controls of the Inspection2 form carry `=Abs([alt]-[rlt])` and
 * `=Abs([alb]-[rlb])` as their expression.
 */
export const legDeltas = (values: Record<string, number>) => {
	const at = values.ALT;
	const rt = values.RLT;
	const ab = values.ALB;
	const rb = values.RLB;

	return {
		top: at !== undefined && rt !== undefined ? Math.abs(at - rt) : null,
		bottom: ab !== undefined && rb !== undefined ? Math.abs(ab - rb) : null,
	};
};
